import { isFaceId, sealById } from '@toiyp/shared';
import type { AvatarView } from '../profiles/api';
import { faceImageUrl } from '../profiles/faces';
import { drawQr, qrMatrix } from './qr';
import { toiypAddress } from './share';

/**
 * The story card (slice 3): a 1080×1920 image drawn in the browser — blush
 * gradient, avatar, name, "Don't text me, toiyp me", a QR code and the
 * address — ready for an Instagram or WhatsApp story. No server rendering.
 */

export const STORY_W = 1080;
export const STORY_H = 1920;

const INK = '#2C2420';
const INK_SOFT = '#6E635C';
const FONT = "Inter, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";

function waxGradient(ctx: CanvasRenderingContext2D, x0: number, x1: number): CanvasGradient {
  const g = ctx.createLinearGradient(x0, 0, x1, 0);
  g.addColorStop(0, '#C8907C');
  g.addColorStop(0.5, '#A87060');
  g.addColorStop(1, '#7B5244');
  return g;
}

/** Centred text that shrinks until it fits `maxWidth`. */
function fitText(
  ctx: CanvasRenderingContext2D,
  text: string,
  y: number,
  weight: number,
  size: number,
  maxWidth: number,
  fill: string | CanvasGradient | ((width: number) => CanvasGradient),
): void {
  let px = size;
  ctx.font = `${weight} ${px}px ${FONT}`;
  while (ctx.measureText(text).width > maxWidth && px > 24) {
    px -= 2;
    ctx.font = `${weight} ${px}px ${FONT}`;
  }
  const w = ctx.measureText(text).width;
  ctx.fillStyle = typeof fill === 'function' ? fill(w) : fill;
  ctx.fillText(text, STORY_W / 2, y);
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function drawAvatar(
  ctx: CanvasRenderingContext2D,
  name: string,
  avatar: AvatarView | null,
  cx: number,
  cy: number,
  r: number,
): Promise<void> {
  // A white ring, like the app's story row.
  ctx.save();
  ctx.shadowColor = 'rgba(44, 36, 32, 0.18)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 12;
  ctx.beginPath();
  ctx.arc(cx, cy, r + 14, 0, Math.PI * 2);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.restore();

  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();

  const url =
    avatar?.kind === 'upload' ? avatar.url : avatar?.kind === 'face' && isFaceId(avatar.url) ? faceImageUrl(avatar.url) : null;
  const img = url ? await loadImage(url) : null;
  const seal = avatar?.kind === 'seal' ? sealById(avatar.url) : null;

  if (img) {
    ctx.drawImage(img, cx - r, cy - r, r * 2, r * 2);
  } else if (seal) {
    ctx.fillStyle = '#FDF0E8';
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.font = `${Math.round(r * 1.05)}px ${FONT}`;
    ctx.textBaseline = 'middle';
    ctx.fillText(seal.glyph, cx, cy + r * 0.05);
    ctx.textBaseline = 'alphabetic';
  } else {
    ctx.fillStyle = waxGradient(ctx, cx - r, cx + r);
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.fillStyle = '#FFFFFF';
    ctx.font = `700 ${Math.round(r)}px ${FONT}`;
    ctx.textBaseline = 'middle';
    ctx.fillText((name[0] ?? '?').toUpperCase(), cx, cy + r * 0.04);
    ctx.textBaseline = 'alphabetic';
  }
  ctx.restore();
}

export interface StoryCardInput {
  displayName: string;
  handle: string;
  avatar: AvatarView | null;
  /** Where the QR code leads: the member's invite link, tagged `via=story`. */
  qrUrl: string;
}

export async function drawStoryCard(canvas: HTMLCanvasElement, input: StoryCardInput): Promise<void> {
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas unavailable');
  // Inter comes from Google Fonts; wait so the card isn't drawn in a fallback.
  try {
    await document.fonts?.load(`900 100px Inter`);
    await document.fonts?.load(`600 40px Inter`);
  } catch {
    // The system font is fine too.
  }

  // The blush ground, with a warm glow behind the avatar.
  const bg = ctx.createLinearGradient(0, 0, STORY_W * 0.4, STORY_H);
  bg.addColorStop(0, '#FDF0E8');
  bg.addColorStop(0.48, '#F3EBE1');
  bg.addColorStop(1, '#EDE0D8');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, STORY_W, STORY_H);
  const glow = ctx.createRadialGradient(540, 560, 40, 540, 560, 620);
  glow.addColorStop(0, 'rgba(200, 144, 124, 0.32)');
  glow.addColorStop(1, 'rgba(200, 144, 124, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  ctx.textAlign = 'center';

  // The wordmark.
  fitText(ctx, 'toiyp', 250, 900, 150, 800, (w) => waxGradient(ctx, 540 - w / 2, 540 + w / 2));

  await drawAvatar(ctx, input.displayName || input.handle, input.avatar, 540, 540, 160);

  fitText(ctx, input.displayName || `@${input.handle}`, 830, 700, 68, 900, INK);

  // The line.
  fitText(ctx, "Don't text me,", 980, 800, 104, 920, INK);
  fitText(ctx, 'toiyp me.', 1095, 900, 112, 920, (w) => waxGradient(ctx, 540 - w / 2, 540 + w / 2));

  // The QR code on a white card.
  const card = { x: 260, y: 1170, w: 560, h: 560 };
  ctx.save();
  ctx.shadowColor = 'rgba(44, 36, 32, 0.16)';
  ctx.shadowBlur = 48;
  ctx.shadowOffsetY = 16;
  roundRect(ctx, card.x, card.y, card.w, card.h, 56);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = INK;
  drawQr(ctx, qrMatrix(input.qrUrl), card.x + 50, card.y + 50, card.w - 100);

  fitText(ctx, toiypAddress(input.handle), 1825, 800, 64, 940, (w) => waxGradient(ctx, 540 - w / 2, 540 + w / 2));
  fitText(ctx, 'Scan to write me a proper letter', 1885, 600, 36, 900, INK_SOFT);
}

/** The card as a PNG file, ready for navigator.share or a download. */
export async function storyCardFile(input: StoryCardInput): Promise<File> {
  const canvas = document.createElement('canvas');
  await drawStoryCard(canvas, input);
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('could not draw the card'))), 'image/png'),
  );
  return new File([blob], `toiyp-${input.handle}.png`, { type: 'image/png' });
}
